import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { EmailFormSchema } from "~/components/auth/email-link-form";
import {
  AuthCard,
  AuthIntro,
  AuthLayout,
  AuthLinks,
} from "~/components/shell/auth-layout";
import { Button } from "~/components/ui/button";
import { Callout } from "~/components/ui/callout";
import { Field } from "~/components/ui/field";
import { Input } from "~/components/ui/input";
import { textLinkClass } from "~/components/ui/text-button";
import { ApiError } from "~/lib/api";
import { meQueryKey, meQueryOptions } from "~/lib/api/me";
import { completeSignIn, isSignInLink, recallSignInEmail } from "~/lib/auth";

export const Route = createFileRoute("/(public)/inloggen/bevestigen")({
  validateSearch: z.object({ enroll: z.string().optional().catch(undefined) }),
  component: ConfirmPage,
});

function ConfirmPage() {
  const { enroll } = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [link] = useState(() => window.location.href);
  const [linkValid] = useState(() => isSignInLink(link));
  const [remembered] = useState(() => recallSignInEmail());
  const startedRef = useRef(false);

  const signIn = useMutation({
    mutationFn: async (email: string): Promise<"home" | "enrol"> => {
      await completeSignIn(email, link);
      queryClient.removeQueries({ queryKey: meQueryKey });
      if (!enroll) return "home";
      try {
        await queryClient.query(meQueryOptions());
        return "home";
      } catch (error) {
        if (error instanceof ApiError && error.status === 403) return "enrol";
        throw error;
      }
    },
    onSuccess: (destination) =>
      destination === "enrol" && enroll
        ? navigate({
            to: "/aanmelden/$enrollCode/gegevens",
            params: { enrollCode: enroll },
            replace: true,
          })
        : navigate({ to: "/", replace: true }),
  });
  const { mutate } = signIn;

  useEffect(() => {
    if (startedRef.current || !linkValid || !remembered) return;
    startedRef.current = true;
    mutate(remembered);
  }, [linkValid, remembered, mutate]);

  const failure = !linkValid
    ? "Deze inloglink is ongeldig of verlopen. Vraag een nieuwe aan."
    : signIn.isError
      ? signIn.error instanceof ApiError
        ? "Inloggen is gelukt, maar je account kon niet worden geladen. Probeer het opnieuw."
        : signInErrorMessage(signIn.error)
      : null;

  if (failure) {
    return (
      <AuthLayout>
        <AuthCard>
          <AuthIntro title="Inloggen niet gelukt" />
          <Callout tone="danger">{failure}</Callout>
        </AuthCard>
        <AuthLinks>
          <Link to="/inloggen" className={textLinkClass}>
            Nieuwe inloglink aanvragen
          </Link>
        </AuthLinks>
      </AuthLayout>
    );
  }

  if (remembered || !signIn.isIdle) {
    return (
      <AuthLayout>
        <AuthCard aria-busy="true">
          <AuthIntro title="Je wordt ingelogd">Even geduld.</AuthIntro>
        </AuthCard>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <AuthCard>
        <AuthIntro title="Bevestig je e-mailadres">
          Je opent de inloglink op een ander apparaat dan waarop je hem aanvroeg. Vul ter
          controle het e-mailadres in waarop je de mail ontving.
        </AuthIntro>
        <ConfirmEmailForm
          onConfirm={(email) => {
            startedRef.current = true;
            mutate(email);
          }}
        />
      </AuthCard>
    </AuthLayout>
  );
}

function ConfirmEmailForm({ onConfirm }: { onConfirm: (email: string) => void }) {
  const form = useForm({
    resolver: zodResolver(EmailFormSchema),
    defaultValues: { email: "" },
  });

  return (
    <form
      onSubmit={form.handleSubmit(({ email }) => onConfirm(email))}
      noValidate
      className="flex flex-col gap-6"
    >
      <Field label="E-mailadres" error={form.formState.errors.email?.message}>
        <Input
          type="email"
          autoComplete="email"
          inputMode="email"
          autoFocus
          {...form.register("email")}
        />
      </Field>
      <Button type="submit" variant="primary" size="block">
        Inloggen
      </Button>
    </form>
  );
}

function signInErrorMessage(error: unknown): string {
  const code =
    typeof error === "object" && error !== null && "code" in error
      ? String(error.code)
      : "";

  switch (code) {
    case "portal/missing-tenant":
      return "Deze inloglink is onvolledig. Vraag een nieuwe aan.";
    case "auth/invalid-action-code":
    case "auth/expired-action-code":
      return "Deze inloglink is al gebruikt of verlopen. Vraag een nieuwe aan.";
    case "auth/invalid-email":
      return "Dit e-mailadres hoort niet bij deze inloglink. Gebruik het adres waarop je de mail hebt ontvangen.";
    case "auth/user-disabled":
      return "Dit account is uitgeschakeld. Neem contact op met Lekkerladen.";
    case "auth/network-request-failed":
      return "We konden geen verbinding maken. Controleer je internet en probeer het opnieuw.";
    default:
      return "Inloggen is niet gelukt. Probeer het opnieuw of vraag een nieuwe inloglink aan.";
  }
}
